import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import AdminLayout from '@/components/layout/AdminLayout';
import TeacherLayout from '@/components/layout/TeacherLayout';
import StudentLayout from '@/components/layout/StudentLayout';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import UsersPage from '@/pages/UsersPage';
import UserDetailPage from '@/pages/UserDetailPage';
import TeachersPage from '@/pages/TeachersPage';
import StudentsPage from '@/pages/StudentsPage';
import CurriculumPage from '@/pages/CurriculumPage';
import ConfigPage from '@/pages/ConfigPage';
import NotificationsPage from '@/pages/NotificationsPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import ManageAdminsPage from '@/pages/ManageAdminsPage';
import FeatureFlagsPage from '@/pages/FeatureFlagsPage';
import AppVersionsPage from '@/pages/AppVersionsPage';
import SystemHealthPage from '@/pages/SystemHealthPage';
import ImportBulkPage from '@/pages/ImportBulkPage';
import QuestionLibraryPage from '@/pages/QuestionLibraryPage';
import HelpRequestsPage from '@/pages/HelpRequestsPage';
import BlockedDomainsPage from '@/pages/BlockedDomainsPage';
import StudentModerationPage from '@/pages/StudentModerationPage';
import PendingTeachersPage from '@/pages/PendingTeachersPage';
import BulkImportStudentsPage from '@/pages/BulkImportStudentsPage';
import ParentManagementPage from '@/pages/ParentManagementPage';
// Teacher pages
import TeacherDashboardPage from '@/pages/teacher/DashboardPage';
import TeacherDecksPage from '@/pages/teacher/DecksPage';
import DeckEditorPage from '@/pages/teacher/DeckEditorPage';
import ImportPage from '@/pages/teacher/ImportPage';
import TeacherStudentsPage from '@/pages/teacher/StudentsPage';
import StudentDetailPage from '@/pages/teacher/StudentDetailPage';
import TeacherAnalyticsPage from '@/pages/teacher/AnalyticsPage';
import TeacherProfilePage from '@/pages/teacher/ProfilePage';
import TeacherLibraryPage from '@/pages/teacher/LibraryPage';
import TeacherHelpRequestsPage from '@/pages/teacher/HelpRequestsPage';
// Student pages
import StudentHomePage from '@/pages/student/HomePage';
import StudentDecksPage from '@/pages/student/DecksPage';
import StudentTeachersPage from '@/pages/student/TeachersPage';
import CardReviewPage from '@/pages/student/CardReviewPage';
import SessionCompletePage from '@/pages/student/SessionCompletePage';
import StudentProgressPage from '@/pages/student/ProgressPage';
import StudentProfilePage from '@/pages/student/ProfilePage';

function PrivateRoute({ children, superAdminOnly = false }: { children: React.ReactNode; superAdminOnly?: boolean }) {
  const { user, token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (superAdminOnly && user?.role !== 'SUPER_ADMIN') {
    // Redirect to appropriate home page based on role
    if (user?.role === 'TEACHER') return <Navigate to="/teacher" replace />;
    if (user?.role === 'STUDENT') return <Navigate to="/student" replace />;
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (!['ADMIN', 'SUPER_ADMIN'].includes(user?.role || '')) {
    // Redirect to appropriate home page based on role
    if (user?.role === 'TEACHER') return <Navigate to="/teacher" replace />;
    if (user?.role === 'STUDENT') return <Navigate to="/student" replace />;
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function TeacherRoute({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== 'TEACHER') {
    // Redirect to appropriate home page based on role
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
    if (user?.role === 'STUDENT') return <Navigate to="/student" replace />;
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function StudentRoute({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== 'STUDENT') {
    // Redirect to appropriate home page based on role
    if (user?.role === 'TEACHER') return <Navigate to="/teacher" replace />;
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function CatchAllRoute() {
  const { user, token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role === 'TEACHER') return <Navigate to="/teacher" replace />;
  if (user?.role === 'STUDENT') return <Navigate to="/student" replace />;
  return <Navigate to="/admin" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signin" element={<Navigate to="/login" replace />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="teachers" element={<TeachersPage />} />
          <Route path="students" element={<StudentsPage />} />
          <Route path="parents" element={<ParentManagementPage />} />
          <Route path="curriculum" element={<CurriculumPage />} />
          <Route path="config" element={<ConfigPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="import" element={<ImportBulkPage />} />
          <Route path="library" element={<QuestionLibraryPage />} />
          <Route path="help-requests" element={<HelpRequestsPage />} />
          <Route path="moderation/blocked-domains" element={<BlockedDomainsPage />} />
          <Route path="moderation/students" element={<StudentModerationPage />} />
          <Route path="teachers/pending" element={<PendingTeachersPage />} />
          <Route path="students/bulk-import" element={<BulkImportStudentsPage />} />
          <Route
            path="super/admins"
            element={
              <PrivateRoute superAdminOnly>
                <ManageAdminsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="super/feature-flags"
            element={
              <PrivateRoute superAdminOnly>
                <FeatureFlagsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="super/app-versions"
            element={
              <PrivateRoute superAdminOnly>
                <AppVersionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="super/system"
            element={
              <PrivateRoute superAdminOnly>
                <SystemHealthPage />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Teacher Routes */}
        <Route
          path="/teacher"
          element={
            <TeacherRoute>
              <TeacherLayout />
            </TeacherRoute>
          }
        >
          <Route index element={<TeacherDashboardPage />} />
          <Route path="decks" element={<TeacherDecksPage />} />
          <Route path="decks/new" element={<DeckEditorPage />} />
          <Route path="decks/import" element={<ImportPage />} />
          <Route path="decks/:id/edit" element={<DeckEditorPage />} />
          <Route path="students" element={<TeacherStudentsPage />} />
          <Route path="students/:id" element={<StudentDetailPage />} />
          <Route path="analytics" element={<TeacherAnalyticsPage />} />
          <Route path="library" element={<TeacherLibraryPage />} />
          <Route path="help-requests" element={<TeacherHelpRequestsPage />} />
          <Route path="profile" element={<TeacherProfilePage />} />
        </Route>

        {/* Student Routes */}
        <Route
          path="/student"
          element={
            <StudentRoute>
              <StudentLayout />
            </StudentRoute>
          }
        >
          <Route index element={<StudentHomePage />} />
          <Route path="decks" element={<StudentDecksPage />} />
          <Route path="teachers" element={<StudentTeachersPage />} />
          <Route path="decks/:deckId/review" element={<CardReviewPage />} />
          <Route path="sessions/:sessionId/complete" element={<SessionCompletePage />} />
          <Route path="progress" element={<StudentProgressPage />} />
          <Route path="profile" element={<StudentProfilePage />} />
        </Route>

        <Route path="*" element={<CatchAllRoute />} />
      </Routes>
    </BrowserRouter>
  );
}
